import { upperFirst } from "lodash";
import dayjs from "../../dayjs";
import type { OrdersTrendGranularity } from "./statisticsApi";

export function formatEur(value: number | null): string {
  if (value === null) {
    return "—";
  }
  return `${Math.round(value).toLocaleString("pl-PL")} EUR`;
}

export function formatPercent(value: number | null): string {
  if (value === null) {
    return "—";
  }
  return `${(Math.round(value * 10) / 10).toLocaleString("pl-PL")}%`;
}

export function formatDays(value: number | null): string {
  if (value === null) {
    return "—";
  }
  const rounded = Math.round(value);
  return `${rounded} ${rounded === 1 ? "dzień" : "dni"}`;
}

export interface PolishNounForms {
  readonly one: string;
  readonly few: string;
  readonly many: string;
}

export const GROUP_FORMS: PolishNounForms = { one: "grupa", few: "grupy", many: "grup" };
export const CALCULATION_FORMS: PolishNounForms = { one: "kalkulacja", few: "kalkulacje", many: "kalkulacji" };

export function formatPolishCount(count: number, forms: PolishNounForms): string {
  return `${count} ${polishNounForm(count, forms)}`;
}

function polishNounForm(count: number, forms: PolishNounForms): string {
  if (count === 1) {
    return forms.one;
  }
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  if (lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14)) {
    return forms.few;
  }
  return forms.many;
}

export function formatDuration(totalSeconds: number): string {
  const totalMinutes = Math.round(totalSeconds / 60);
  return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}min`;
}

/** Short Polish axis label for an orders-trend bucket key (`YYYY-MM-DD`, `YYYY-MM` or `YYYY`). Month labels get
 * a two-digit year suffix when the chart spans more than one year. */
export function formatBucketLabel(key: string, granularity: OrdersTrendGranularity, spansYears: boolean): string {
  if (granularity === "YEAR") {
    return key;
  }
  const date = dayjs(key);
  if (granularity === "DAY") {
    return date.format("dd D");
  }
  return upperFirst(date.format(spansYears ? "MMM YY" : "MMM"));
}

export interface ContactCalculations {
  readonly count: number;
  readonly byContactPersonCount: number;
  readonly byContactPersonPercent: number;
}

export function formatContactCalculations(calculations: ContactCalculations): string {
  return `${calculations.count} · ${calculations.byContactPersonCount} (${formatPercent(calculations.byContactPersonPercent)}) przez kontakt`;
}

export function formatActiveFilters(subject: string, from: string, to: string): string {
  return `${subject} · ${from} — ${to}`;
}
